import { ServiceProvider } from "@/models/serviceprovider";
import { ContentProvider } from "@/models/contentprovider";

export interface IAuthorizationPolicyDocument {
    name?: string | null;
    description?: string | null;
    uuid?: string | null;
    serviceProviderUuid?: string | null;
    contentProviderUuid?: string | null;
    policyDocument?: IAuthorizationPolicy | null;
}

export interface IAuthorizationPolicy {
    serviceProviders?: IServiceProviderAuthorizationPolicy[] | string | null;
}

export interface IServiceProviderAuthorizationPolicy {
    uuid?: string | null;
    contentProviders?: IContentProviderAuthorizationPolicy[] | string | null;
    serviceProvider?: ServiceProvider | null;
}

export interface IContentProviderAuthorizationPolicy {
    uuid?: string | null;
    programs?: IProgramAuthorizationPolicy[] | string | null;
    contentProvider?: ContentProvider | null;
}

export interface IProgramAuthorizationPolicy {
    uuid: string;
}

export class AuthorizationPolicyDocument {
    public name: string;
    public description: string | null;
    public uuid: string | null;
    public serviceProviderUuid: string | null;
    public contentProviderUuid: string | null;
    public policyDocument: AuthorizationPolicy;

    constructor(authorizationPolicyDocument?: IAuthorizationPolicyDocument | AuthorizationPolicyDocument | null) {
        if (!authorizationPolicyDocument) {
            throw new Error("Cannot create authorization policy from falsey IAuthorizationPolicy");
        }
        else if (!authorizationPolicyDocument.name) {
            throw new Error("Policy document must have a name.");
        }
        else if (!(authorizationPolicyDocument.policyDocument)) {
            throw new Error("Cannot create authorization policy where policy document does not exist.");
        }

        this.name = authorizationPolicyDocument.name;
        this.uuid = authorizationPolicyDocument.uuid || null;
        this.description = authorizationPolicyDocument.description || null;
        this.serviceProviderUuid = authorizationPolicyDocument.serviceProviderUuid || null;
        this.contentProviderUuid = authorizationPolicyDocument.contentProviderUuid || null;
        this.policyDocument = new AuthorizationPolicy(authorizationPolicyDocument!.policyDocument);
    }

    public toUnscopedDto(): IAuthorizationPolicyDocument {
        if (this.serviceProviderUuid) {
            throw new Error("serviceProviderUuid must NOT have a value");
        }
        else if (this.contentProviderUuid) {
            throw new Error("contentProviderUuid must NOT have a value");
        }

        return {
            name: this.name,
            description: this.description,
            serviceProviderUuid: undefined,
            contentProviderUuid: undefined,
            policyDocument: this.policyDocument.toDto()
        };
    }

    public toServiceProviderScopedDto(serviceProviderId: any): IAuthorizationPolicyDocument {
        if (!this.serviceProviderUuid) {
            throw new Error("serviceProviderUuid must have a value");
        }
        else if (this.contentProviderUuid) {
            throw new Error("contentProviderUuid must NOT have a value");
        }
        else if (this.policyDocument.serviceProviders.isStar) {
            throw new Error("service provider policy must not be star");
        }
        else if (!this.policyDocument.serviceProviders.policies ||
            this.policyDocument.serviceProviders.policies.length !== 1 ||
            this.policyDocument.serviceProviders.policies.findIndex((x) => x.uuid !== serviceProviderId) >= 0) {

            throw new Error(`service provider policy must have exactly one service provider in it: ${serviceProviderId}`);
        }
        else if (serviceProviderId !== this.serviceProviderUuid) {
            throw new Error(`service provider id mismatch ${serviceProviderId} and ${this.serviceProviderUuid}`);
        }

        return {
            name: this.name,
            description: this.description,
            serviceProviderUuid: this.serviceProviderUuid,
            contentProviderUuid: undefined,
            uuid: this.uuid || undefined,
            policyDocument: this.policyDocument.toDto()
        };
    }

    public isUnscoped(): boolean {
        return !this.serviceProviderUuid && !this.contentProviderUuid;
    }

    public isServiceProviderScoped(): boolean {
        return !!this.serviceProviderUuid && !this.contentProviderUuid;
    }

    public isContentProviderScoped(): boolean {
        return !!this.serviceProviderUuid && !!this.contentProviderUuid;
    }

    public get hasServiceProviderScopes(): boolean {
        return !this.policyDocument.serviceProviders.isStar;
    }

    public get hasContentProviderScopes(): boolean {
        if (this.policyDocument.serviceProviders.isStar) {
            return false;
        }

        for (const serviceProviderPolicy of this.policyDocument.serviceProviders.policies) {
            if (!serviceProviderPolicy.contentProviders.isStar) {
                return true;
            }
        }

        return false;
    }

    public get hasProgramScopes(): boolean {
        if (this.policyDocument.serviceProviders.isStar) {
            return false;
        }

        for (const serviceProviderPolicy of this.policyDocument.serviceProviders.policies) {
            if (!serviceProviderPolicy.contentProviders.isStar) {
                for (const contentProviderPolicy of serviceProviderPolicy.contentProviders.policies) {
                    if (!contentProviderPolicy.programs.isStar) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    public isSameScopeLevel(other: AuthorizationPolicyDocument): boolean {
        return this.hasServiceProviderScopes === other.hasServiceProviderScopes &&
            this.hasContentProviderScopes === other.hasContentProviderScopes &&
            this.hasProgramScopes === other.hasProgramScopes;
    }

    public static emptyUnscoped(email: any): AuthorizationPolicyDocument {
        return new AuthorizationPolicyDocument({
            name: "Policy " + email,
            description: "Generated for " + email,
            policyDocument: {
                serviceProviders: "*"
            }
        });
    }

    public static empty(email: any): AuthorizationPolicyDocument {
        return new AuthorizationPolicyDocument({
            name: "Policy " + email,
            description: "Generated for " + email,
            policyDocument: {
                serviceProviders: []
            }
        });
    }

    public static emptyServiceProviderScoped(serviceProviderUuid: any, email: any): AuthorizationPolicyDocument {
        return new AuthorizationPolicyDocument({
            name: "Policy " + email,
            description: "Generated for " + email,
            serviceProviderUuid: serviceProviderUuid,
            policyDocument: {
                serviceProviders: [{
                    uuid: serviceProviderUuid,
                    contentProviders: "*"
                }]
            }
        });
    }
    public static emptyCPProviderScoped(serviceProviderUuid: any, contentProviderUuid: any, email: any): AuthorizationPolicyDocument {
        var cp = new ContentProviderAuthorizationPolicy({
            contentProvider: contentProviderUuid, 
            uuid: contentProviderUuid,
            programs: "*"
        })
       
     
        var cpsp = [] as IContentProviderAuthorizationPolicy[];
     
       // var s = cp.toDto();
        cpsp.push( cp.toDto() );
       var apc = new AuthorizationPolicyDocument({
            name: "Policy " + email,
            description: "Generated for " + email,
            serviceProviderUuid: serviceProviderUuid,
            policyDocument: {
                serviceProviders: [{
                    uuid: serviceProviderUuid,
                    contentProviders: cpsp,
                   
                }]
            }
        });
        console.log(JSON.stringify(apc))
        return apc;
    }
}

export class AuthorizationPolicy {
    public serviceProviders: DictionaryOrStar<ServiceProviderAuthorizationPolicy>;

    public constructor(authorizationPolicy?: IAuthorizationPolicy | AuthorizationPolicy | null) {
        if (!authorizationPolicy) {
            throw new Error("Cannot create authorization policy from falsey authorization policy.");
        }

        this.serviceProviders = new DictionaryOrStar();
        if (!authorizationPolicy.serviceProviders) {
            throw new Error("Cannot create authorization policy where policy document has no service providers property.");
        }

        if (authorizationPolicy instanceof AuthorizationPolicy) {
            if (authorizationPolicy.serviceProviders.isStar) {
                this.serviceProviders.isStar = true;
            }
            else {
                for (const serviceProviderPolicy of authorizationPolicy.serviceProviders.policies) {
                    this.serviceProviders.add(new ServiceProviderAuthorizationPolicy(serviceProviderPolicy));
                }
            }
        }
        else {
            if (typeof authorizationPolicy.serviceProviders === "string") {
                if (authorizationPolicy.serviceProviders === "*") {
                    this.serviceProviders.isStar = true;
                }
                else {
                    throw new Error("Invalid string value for service providers property: " + authorizationPolicy.serviceProviders);
                }
            }
            else if (authorizationPolicy.serviceProviders instanceof Array) {
                for (const maybeServiceProviderPolicy of authorizationPolicy.serviceProviders) {
                    const serviceProviderPolicy = new ServiceProviderAuthorizationPolicy(maybeServiceProviderPolicy);
                    this.serviceProviders.add(serviceProviderPolicy);
                }
            }
            else {
                throw new Error("Invalid type for policyDocument.serviceProviders: " + authorizationPolicy.serviceProviders);
            }
        }
    }

    public toDto(): IAuthorizationPolicy {
        let serviceProviders: string | IServiceProviderAuthorizationPolicy[];
        if (this.serviceProviders.isStar) {
            serviceProviders = "*";
        }
        else {
            const value = [] as IServiceProviderAuthorizationPolicy[];
            for (const policy of this.serviceProviders.policies.values()) {
                value.push(policy.toDto());
            }
            serviceProviders = value;
        }

        return {
            serviceProviders: serviceProviders
        };
    }
}

export class DictionaryOrStar<T extends IKeyed> {
    public isStar: boolean = false;
    public policies = [] as T[];

    public add(value: T): void {
        const index = this.policies.findIndex((x) => x.uuid === value.uuid);
        if (index !== -1) {
            this.policies[index] = value;
        }
        else {
            this.policies.push(value);
        }
    }

    public has(key: string): boolean {
        return this.policies.findIndex((x) => x.uuid === key) !== -1;
    }

    public delete(key: string): void {
        const index = this.policies.findIndex((x) => x.uuid === key);
        if (index !== -1) {
            this.policies.splice(index, 1);
        }
    }
}

export class ServiceProviderAuthorizationPolicy implements IKeyed {
    public uuid: string;
    public serviceProvider: ServiceProvider | null = null;
    public contentProviders: DictionaryOrStar<ContentProviderAuthorizationPolicy>;

    public constructor(serviceProviderPolicy?: IServiceProviderAuthorizationPolicy | ServiceProviderAuthorizationPolicy | null) {
        if (!(serviceProviderPolicy)) {
            throw new Error("Cannot create ServiceProviderAuthorizationPolicy from falsey IServiceProviderAuthorizationPolicy.");
        }
        else if (!serviceProviderPolicy.uuid) {
            throw new Error("uuid property must have a value.");
        }
        else if (!(serviceProviderPolicy.contentProviders)) {
            throw new Error("contentProviders property must have a value.");
        }

        this.uuid = serviceProviderPolicy.uuid;
        this.contentProviders = new DictionaryOrStar<ContentProviderAuthorizationPolicy>();
        this.serviceProvider = serviceProviderPolicy.serviceProvider || null;

        if (serviceProviderPolicy instanceof ServiceProviderAuthorizationPolicy) {
            if (serviceProviderPolicy.contentProviders.isStar) {
                this.contentProviders.isStar = true;
            }
            else {
                for (const contentProviderPolicy of serviceProviderPolicy.contentProviders.policies) {
                    this.contentProviders.add(new ContentProviderAuthorizationPolicy(contentProviderPolicy));
                }
            }
        }
        else {
            if (typeof serviceProviderPolicy.contentProviders === "string") {
                if (serviceProviderPolicy.contentProviders === "*") {
                    this.contentProviders.isStar = true;
                }
                else {
                    throw new Error("Invalid string value for content providers property: " + serviceProviderPolicy.contentProviders);
                }
            }
            else if (serviceProviderPolicy.contentProviders instanceof Array) {
                for (const maybeContentProviderPolicy of serviceProviderPolicy.contentProviders) {
                    const contentProviderPolicy = new ContentProviderAuthorizationPolicy(maybeContentProviderPolicy);
                    this.contentProviders.add(contentProviderPolicy);
                }
            }
            else {
                throw new Error("Invalid type for IServiceProviderAuthorizationPolicy.contentProviders: " + serviceProviderPolicy.contentProviders);
            }
        }
    }

    public toDto(): IServiceProviderAuthorizationPolicy {
        let contentProviders: string | IContentProviderAuthorizationPolicy[];
        if (this.contentProviders.isStar) {
            contentProviders = "*";
        }
        else {
            const value = [] as IContentProviderAuthorizationPolicy[];
            for (const policy of this.contentProviders.policies.values()) {
                value.push(policy.toDto());
            }
            contentProviders = value;
        }

        return {
            uuid: this.uuid,
            contentProviders: contentProviders
        };
    }
}

export class ContentProviderAuthorizationPolicy implements IKeyed {
    public uuid: string;
    public programs: DictionaryOrStar<ProgramAuthorizationPolicy>;
    public contentProvider: ContentProvider | null = null;

    public constructor(contentProviderPolicy: IContentProviderAuthorizationPolicy | ContentProviderAuthorizationPolicy) {
        if (!(contentProviderPolicy)) {
            throw new Error("Cannot create ContentProviderAuthorizationPolicy from falsey IContentProviderAuthorizationPolicy.");
        }
        else if (!contentProviderPolicy.uuid) {
            throw new Error("uuid property must have a value.");
        }
        else if (!(contentProviderPolicy.programs)) {
            throw new Error("programs property must have a value.");
        }

        this.uuid = contentProviderPolicy.uuid;
        this.programs = new DictionaryOrStar<ProgramAuthorizationPolicy>();
        this.contentProvider = contentProviderPolicy.contentProvider || null;

        if (contentProviderPolicy instanceof (ContentProviderAuthorizationPolicy)) {
            if (contentProviderPolicy.programs.isStar) {
                this.programs.isStar = true;
            }
            else {
                for (const programPolicy of contentProviderPolicy.programs.policies) {
                    this.programs.add(new ProgramAuthorizationPolicy(programPolicy));
                }
            }
        }
        else {
            if (typeof contentProviderPolicy.programs === "string") {
                if (contentProviderPolicy.programs === "*") {
                    this.programs.isStar = true;
                }
                else {
                    throw new Error("Invalid string value for programs property: " + contentProviderPolicy.programs);
                }
            }
            else if (contentProviderPolicy.programs instanceof Array) {
                for (const maybeProgramPolicy of contentProviderPolicy.programs) {
                    const programPolicy = new ProgramAuthorizationPolicy(maybeProgramPolicy);
                    this.programs.add(programPolicy);
                }
            }
            else {
                throw new Error("Invalid type for IContentProviderAuthorizationPolicy.programs: " + contentProviderPolicy.programs);
            }
        }
    }

    public toDto(): IContentProviderAuthorizationPolicy {
        let programs: string | IProgramAuthorizationPolicy[];
        if (this.programs.isStar) {
            programs = "*";
        }
        else {
            const value = [] as IProgramAuthorizationPolicy[];
            for (const policy of this.programs.policies.values()) {
                value.push(policy.toDto());
            }
            programs = value;
        }

        return {
            uuid: this.uuid,
            programs: programs
        };
    }
}

export class ProgramAuthorizationPolicy implements IKeyed {
    public uuid: string;

    public constructor(programPolicy: IProgramAuthorizationPolicy | ProgramAuthorizationPolicy) {
        if (!(programPolicy)) {
            throw new Error("Cannot create ProgramAuthorizationPolicy from falsey IProgramAuthorizationPolicy.");
        }
        else if (!programPolicy.uuid) {
            throw new Error("uuid property must have a value.");
        }

        this.uuid = programPolicy.uuid;
    }

    public toDto(): IProgramAuthorizationPolicy {
        return {
            uuid: this.uuid
        };
    }
}

export interface IKeyed {
    uuid: string;
}
